import type * as Polymorphic from '@radix-ui/react-polymorphic';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { IconName } from '../../assets/Icon/Icon';
import type {
  PolymorphicComponentProps,
  PolymorphicRef,
} from '../../utilities/types/polymorphicAsProp';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { IconTooltipProps, LabelTooltipProps } from '../Tooltip/Tooltip';
import { Icon } from '../../assets/Icon/Icon';
import { sizing } from '../../common/sizing';
import { selectors } from '../../controls/shared/styles';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import {
  colors,
  css,
  darkThemeSelector,
  fonts,
  fontWeights,
  shadows,
  styled,
} from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { AccessLevel } from '../../utilities/useAccessLevelContext';
import { IconTooltip, LabelTooltip } from '../Tooltip/Tooltip';
import { useCanScrollX } from './useCanScrollX';
import { TableRowSelectedProvider, useTableRowSelected } from './utils';

const ZINDEX_HEAD_LEADING = 20;
const ZINDEX_HEAD_STUCK = 19;
const ZINDEX_HEAD_SORTED = 18;
const ZINDEX_HEAD_INITIAL = 17;

const ZINDEX_BODY_STICKY_SELECTED = 16;
const ZINDEX_BODY_STICKY = 15;

const ZINDEX_BODY_SELECTED = 11;
const ZINDEX_BODY_HOVERED = 5;
const ZINDEX_BODY_INITIAL = 1;
const ZINDEX_BODY_DISABLED = 0;

type TableNestedProp = boolean;
type TableMultiRowSelectionProp = boolean;
export type TableVariantProp = 'application' | 'neutral';

const TableNestedContext = createContext<boolean | undefined>(undefined);
export const TableNestedProvider = TableNestedContext.Provider;

const TableVariantContext = createContext<TableVariantProp | undefined>(undefined);
export const TableVariantProvider = TableVariantContext.Provider;

const TableMultiRowSelectionContext = createContext<boolean | undefined>(undefined);
export const TableMultiRowSelectionProvider = TableMultiRowSelectionContext.Provider;

export const useTableNested = (
  controlledValue?: TableNestedProp,
  defaultValue: TableNestedProp = false,
) => {
  const tableNested = useContext(TableNestedContext);
  return controlledValue ?? tableNested ?? defaultValue;
};

export const useTableVariant = (
  controlledValue?: TableVariantProp,
  defaultValue: TableVariantProp = 'application',
) => {
  const tableVariant = useContext(TableVariantContext);
  return controlledValue ?? tableVariant ?? defaultValue;
};

export const useTableMultiRowSelection = (
  controlledValue?: TableMultiRowSelectionProp,
  defaultValue: TableMultiRowSelectionProp = false,
) => {
  const tableMultiRowSelection = useContext(TableMultiRowSelectionContext);
  return controlledValue ?? tableMultiRowSelection ?? defaultValue;
};

export function TableCellEmpty({ internal }: { internal?: boolean }) {
  return (
    <Text family="monospace" internal={internal} style={{ color: 'currentColor' }}>
      -
    </Text>
  );
}

const tableHeadRowHeight = {
  '@notDesktop': '$32',
  '@desktop': '$28',
};

const tableBodyRowHeight = {
  '@notDesktop': '$40',
  '@desktop': '$36',
};

const TableCellCollapsable = styled(Icon, {
  width: '$12',
  height: '$12',
});

const TableCellContents = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  color: '$$textColor',
  fontFamily: fonts.sans,
  fontWeight: fontWeights.regular,
  fontVariant: 'tabular-nums',
  verticalAlign: 'middle',
  wordBreak: 'keep-all',
  whiteSpace: 'nowrap',
  cursor: 'default',
  ...BodySansSizes,

  '@notDesktop': {
    heightAll: tableBodyRowHeight['@notDesktop'],
  },

  '@desktop': {
    heightAll: tableBodyRowHeight['@desktop'],
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    alignment: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
    hasField: {
      true: {
        '@notDesktop': {
          padding: '$8 $8',
        },

        '@desktop': {
          padding: '$6 $4',
        },
      },
      false: {
        '@notDesktop': {
          padding: '$8 $16',
        },

        '@desktop': {
          padding: '$6 $12',
        },
      },
    },
    hasNested: {
      true: {
        padding: 0,
        heightAll: 'auto',
        maxHeight: 'none',

        [selectors.focus]: {
          '&::after': {
            display: 'none',
          },
        },
      },
      false: {},
    },
    head: {
      true: {
        minHeight: 'auto',
      },
      false: {},
    },
    isLeading: {
      true: {
        fontWeight: fontWeights.bold,
      },
      false: {},
    },
    isNavigable: {
      true: {
        cursor: 'pointer',

        [selectors.focus]: {
          '&::before': {
            content: '',
            position: 'absolute',
            inset: '$4',
            display: 'block',
            boxShadow: shadows.focusRingLight,
            borderRadius: '$8',

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    internal: {
      true: {
        color: '$$internalBodyLight',

        [darkThemeSelector]: {
          color: '$$internalBodyDark',
        },
      },
      false: {},
    },
    stuck: {
      top: {},
      right: {},
      bottom: {},
      left: {},
    },
    variant: {
      application: {},
      neutral: {},
    },
  },
  compoundVariants: [
    {
      variant: 'application',
      isLeading: true,
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'top',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'right',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'bottom',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'left',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'neutral',
      isLeading: true,
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'top',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'right',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'bottom',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'left',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      isLeading: true,
      isNavigable: true,
      css: {
        color: '$$leadingTextColor',

        [selectors.hover]: {
          color: colors.linkAlternativeInteractiveLight,

          [darkThemeSelector]: {
            color: colors.linkAlternativeInteractiveDark,
          },
        },
      },
    },
  ],
});

const TableCellContainer = styled('td', {
  position: 'relative',
  zIndex: ZINDEX_BODY_INITIAL,
  padding: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  variants: {
    collapsable: {
      true: {
        width: '$36',
      },
      false: {},
    },
    hasNested: {
      true: {
        padding: '0 0 $16 $36',
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    isLeading: {
      true: {
        position: 'sticky',
        left: 0,
      },
      false: {},
    },
    isSelected: {
      true: {
        zIndex: ZINDEX_BODY_SELECTED,
      },
      false: {},
    },
    stuck: {
      top: {
        position: 'sticky',
        top: 0,
      },
      right: {
        position: 'sticky',
        right: 0,
      },
      bottom: {
        position: 'sticky',
        bottom: 0,
      },
      left: {
        position: 'sticky',
        left: 0,
      },
    },
    isMultiRowSelection: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      isSelected: true,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY_SELECTED,
      },
    },
    {
      isNested: false,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'top',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'right',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'bottom',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'left',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'top',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'right',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'bottom',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'left',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isLeading: true,
      isMultiRowSelection: true,
      css: {
        left: '46px',
      },
    },
  ],
});

interface TableCellProps {
  /**
   * The alignment of the cell content.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * Whether the cell should be collapsable.
   */
  collapsable?: boolean;
  /**
   * Whether the cell is collapsed.
   */
  collapsed?: boolean;
  /**
   * The number of columns the cell should span.
   */
  colSpan?: number;
  /**
   * Whether the cell has a nested `Table`.
   */
  hasNested?: boolean;
  /**
   * Whether the cell is the leading cell in the row.
   */
  isLeading?: boolean;
  /**
   * Whether the cell has a field.
   */
  hasField?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * Whether the cell is navigable and should show it can be clicked/tapped.
   */
  isNavigable?: boolean;
  /**
   * Whether the cell is stuck to the top, right, bottom, or left of the `Table`.
   */
  stuck?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Access level for the cell.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Whether the cell is visible only to Meter employees.
   */
  internal?: boolean;
  /**
   * The group the cell belongs to.
   */
  group?: string | null;
}

function TableCellPassthrough<Tag extends React.ElementType>(
  {
    as,
    alignment,
    children,
    collapsed,
    collapsable,
    colSpan,
    hasField = false,
    hasNested,
    isLeading,
    isNavigable,
    isNested,
    isSelected,
    multiRowSelection,
    stuck,
    to,
    internal,
    accessLevel,
    group,
    hotkeyTos,
    ...props
  }: PolymorphicComponentProps<Tag, TableCellProps>,
  ref: PolymorphicRef<Tag>,
) {
  const tableVariant = useTableVariant();
  const tableNested = useTableNested(isNested);
  const tableMultiRowSelection = useTableMultiRowSelection(multiRowSelection);
  const tableRowSelected = useTableRowSelected(isSelected);
  return (
    <TableCellContainer
      collapsable={collapsable}
      colSpan={colSpan}
      hasNested={hasNested}
      isNested={tableNested}
      isMultiRowSelection={tableMultiRowSelection}
      isLeading={isLeading}
      isSelected={tableRowSelected}
      stuck={stuck}
    >
      <TableCellContents
        as={collapsable ? 'button' : as}
        to={to}
        alignment={alignment}
        hasField={hasField}
        hasNested={hasNested}
        isLeading={isLeading}
        isNavigable={!!to || isNavigable || collapsable}
        isNested={tableNested}
        stuck={stuck}
        tabIndex={collapsable || (isLeading && to) ? 0 : undefined}
        data-has-nested={hasNested && 'true'}
        {...props}
        internal={internal ?? (accessLevel ? accessLevel === AccessLevel.Internal : undefined)}
        variant={tableVariant}
        ref={ref}
      >
        {collapsable && (
          <TableCellCollapsable icon={collapsed ? 'chevron-right' : 'chevron-down'} />
        )}
        {children}
      </TableCellContents>
    </TableCellContainer>
  );
}

export const TableCell = React.forwardRef(TableCellPassthrough) as Polymorphic.ForwardRefComponent<
  React.ElementType,
  TableCellProps
>;

const stateCellWidth = 8;
const bufferCellWidth = sizing.primary - stateCellWidth;

export const TableCellBufferContainer = styled('td', {
  width: `$${bufferCellWidth}`,
  minWidth: `$${bufferCellWidth}`,
  maxWidth: `$${bufferCellWidth}`,

  variants: {
    head: {
      true: {
        position: 'sticky',
        top: 0,
        zIndex: ZINDEX_HEAD_INITIAL,

        '@notDesktop': {
          heightAll: tableHeadRowHeight['@notDesktop'],
        },

        '@desktop': {
          heightAll: tableHeadRowHeight['@desktop'],
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    side: {
      leading: {},
      trailing: {},
    },
    variant: {
      application: {},
      neutral: {},
    },
  },

  compoundVariants: [
    {
      head: true,
      variant: 'application',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgApplicationLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgApplicationDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'application',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgApplicationLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgApplicationDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgNeutralLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgNeutralDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgNeutralLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgNeutralDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      isNested: true,
      side: 'leading',
      css: {
        strokeTop: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeTop: colors.strokeNeutralDark,
        },
      },
    },
  ],
});

type TableCellBufferProps = {
  /**
   * Whether the cell state is at the top of the table.
   */
  head?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * The side of row the cell appears.
   */
  side?: 'leading' | 'trailing';
};

export function TableCellBuffer({ head, isNested, side, ...remaining }: TableCellBufferProps) {
  const tableNested = useTableNested(isNested);
  const tableVariant = useTableVariant();
  return (
    <TableCellBufferContainer
      head={head}
      isNested={tableNested}
      side={side}
      variant={tableVariant}
      {...remaining}
    />
  );
}

const TableCellStateContents = styled('div', {
  width: '100%',

  '@notDesktop': {
    heightAll: tableBodyRowHeight['@notDesktop'],
  },

  '@desktop': {
    heightAll: tableBodyRowHeight['@desktop'],
  },

  variants: {
    head: {
      true: {
        '@notDesktop': {
          heightAll: tableHeadRowHeight['@notDesktop'],
        },

        '@desktop': {
          heightAll: tableHeadRowHeight['@desktop'],
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    side: {
      leading: {
        borderRadius: '$10 0 0 $10',
      },
      trailing: {
        borderRadius: '0 $10 $10 0',
      },
    },
  },
  compoundVariants: [
    {
      head: true,
      css: {
        borderRadius: 0,
      },
    },
    {
      head: true,
      isNested: true,
      side: 'leading',
      css: {
        strokeTop: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeTop: colors.strokeNeutralDark,
        },
      },
    },
  ],
});

const TableCellStateContainer = styled('td', {
  padding: 0,
  width: `$${stateCellWidth}`,
  minWidth: `$${stateCellWidth}`,
  maxWidth: `$${stateCellWidth}`,

  variants: {
    head: {
      true: {
        position: 'sticky',
        top: 0,
        zIndex: ZINDEX_HEAD_INITIAL,
      },
      false: {},
    },
    variant: {
      application: {},
      neutral: {},
    },
  },
  compoundVariants: [
    {
      head: true,
      variant: 'application',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
  ],
});

type TableCellStateProps = {
  /**
   * Whether the cell state is at the top of the table.
   */
  head?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * The side of row the cell appears.
   */
  side?: 'leading' | 'trailing';
};

export function TableCellState<Tag extends React.ElementType>({
  head,
  isNested,
  side,
  ...remaining
}: PolymorphicComponentProps<Tag, TableCellStateProps>) {
  const tableVariant = useTableVariant();
  const tableNested = useTableNested(isNested);
  return (
    <TableCellStateContainer head={head} data-side={side} variant={tableVariant} {...remaining}>
      <TableCellStateContents head={head} isNested={tableNested} side={side} />
    </TableCellStateContainer>
  );
}

const TableRowStyles = css({
  $$iconColor: colors.iconNeutralLight,
  $$textColor: colors.bodyNeutralLight,
  $$leadingTextColor: colors.linkAlternativeInitialLight,

  $$internalBodyLight: colors.internalBodyLight,
  $$internalBodyDark: colors.internalBodyDark,

  position: 'relative',
  width: '100%',

  [darkThemeSelector]: {
    $$iconColor: colors.iconNeutralDark,
    $$textColor: colors.bodyNeutralDark,
    $$leadingTextColor: colors.linkAlternativeInitialDark,
  },

  [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
    strokeTopBottom: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      strokeTopBottom: colors.strokeNeutralDark,
    },
  },
});

const TableRowContainer = styled('tr', TableRowStyles, {
  position: 'relative',
  zIndex: ZINDEX_BODY_INITIAL,

  [selectors.hover]: {
    zIndex: ZINDEX_BODY_HOVERED,
  },

  variants: {
    isDisabled: {
      true: {
        zIndex: ZINDEX_BODY_DISABLED,
        $$iconColor: colors.bodyNeutralLight,
        $$textColor: colors.bodyNeutralLight,
        $$leadingTextColor: colors.bodyNeutralLight,

        opacity: 0.5,

        [darkThemeSelector]: {
          $$iconColor: colors.bodyNeutralDark,
          $$textColor: colors.bodyNeutralDark,
          $$leadingTextColor: colors.bodyNeutralDark,
        },

        [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
          background: colors.bgApplicationLight,
          strokeTopBottom: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            background: colors.bgApplicationDark,
            strokeTopBottom: colors.strokeNeutralDark,
          },
        },

        [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
          background: colors.bgApplicationLight,

          [darkThemeSelector]: {
            background: colors.bgApplicationDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
          strokeNoRight: colors.bgApplicationLight,

          [darkThemeSelector]: {
            strokeNoRight: colors.bgApplicationDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
          strokeNoLeft: colors.bgApplicationLight,

          [darkThemeSelector]: {
            strokeNoLeft: colors.bgApplicationDark,
          },
        },

        [selectors.hover]: {
          zIndex: ZINDEX_BODY_DISABLED,

          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            $$leadingTextColor: colors.bodyNeutralLight,
            background: colors.bgNeutralLight,
            strokeTopBottom: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              $$leadingTextColor: colors.bodyNeutralDark,
              background: colors.bgNeutralDark,
              strokeTopBottom: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            background: colors.bgNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
    },
    isDragging: {
      true: {
        opacity: 0.8,
      },
      false: {},
    },
    isSelected: {
      true: {
        zIndex: ZINDEX_BODY_SELECTED,
        $$iconColor: colors.bodyBrandLight,
        $$textColor: colors.headingBrandLight,
        $$leadingTextColor: colors.headingBrandLight,

        [darkThemeSelector]: {
          $$iconColor: colors.bodyBrandDark,
          $$textColor: colors.headingBrandDark,
          $$leadingTextColor: colors.headingBrandDark,
        },

        [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
          background: colors.bgBrandLight,
          strokeTopBottom: colors.strokeBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeTopBottom: colors.strokeBrandDark,
          },
        },

        [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
          background: colors.bgBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
          strokeNoRight: colors.strokeBrandLight,

          [darkThemeSelector]: {
            strokeNoRight: colors.strokeBrandDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
          strokeNoLeft: colors.strokeBrandLight,

          [darkThemeSelector]: {
            strokeNoLeft: colors.strokeBrandDark,
          },
        },

        [selectors.hover]: {
          zIndex: ZINDEX_BODY_SELECTED,

          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            $$leadingTextColor: colors.headingBrandLight,
            background: colors.bgBrandLight,
            strokeTopBottom: colors.strokeBrandLight,

            [darkThemeSelector]: {
              $$leadingTextColor: colors.headingBrandDark,
              background: colors.bgBrandDark,
              strokeTopBottom: colors.strokeBrandDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            background: colors.bgBrandLight,

            [darkThemeSelector]: {
              background: colors.bgBrandDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            strokeNoRight: colors.strokeBrandLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeBrandDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_SELECTED,
            strokeNoLeft: colors.strokeBrandLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeBrandDark,
            },
          },
        },
      },
      false: {},
    },
    variant: {
      application: {
        [selectors.hover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgNeutralLight,
            strokeTopBottom: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
              strokeTopBottom: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
      neutral: {
        [selectors.hover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgApplicationLight,
            strokeTopBottom: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgApplicationDark,
              strokeTopBottom: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgApplicationLight,

            [darkThemeSelector]: {
              background: colors.bgApplicationDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
    },
  },
});

interface TableRowProps {
  /**
   * The content of the row.
   */
  children?: React.ReactNode | undefined;
  /**
   * Whether the row is being dragged.
   */
  isDragging?: boolean;
  /**
   * Whether the row is navigable and should show it can be clicked/tapped.
   */
  isNavigable?: boolean;
  /**
   * Whether the row is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * Whether the row is selected.
   */
  isSelected?: boolean;
}

function TableRowPassthrough<Tag extends React.ElementType>(
  { isDragging, isNested, isSelected, ...props }: PolymorphicComponentProps<Tag, TableRowProps>,
  ref: PolymorphicRef<Tag>,
) {
  const tableVariant = useTableVariant();
  return (
    <TableRowContainer
      ref={ref}
      {...props}
      isDragging={isDragging}
      isSelected={isSelected}
      variant={tableVariant}
      data-is-selected={isSelected && 'true'}
    >
      <TableRowSelectedProvider value={isSelected}>{props.children}</TableRowSelectedProvider>
    </TableRowContainer>
  );
}

export const TableRow = React.forwardRef(TableRowPassthrough) as Polymorphic.ForwardRefComponent<
  React.ElementType,
  TableRowProps
>;

export const TableBody = styled('tbody');

const TableHeadCellSort = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  width: 0,
  height: '$10',

  variants: {
    isVisible: {
      true: {
        // TRICKY: Toggle visibility rather than display to always reserve
        // space in the layout for the icon.
        visibility: 'visible',
      },
      false: {
        visibility: 'hidden',
      },
    },
  },
  defaultVariants: {
    isVisible: false,
  },
});

const TableHeadCellIcon = styled(Icon, {
  width: '$14',
  height: '$14',
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },
});

const TableHeadCellSortIcon = styled(Icon, {
  display: 'flex',
  marginLeft: '$4',
});

const TableHeadCellBadge = styled('div', {
  marginLeft: '$4',
});

const TableHeadCellContents = styled('div', {
  ...SmallSansSizes,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  strokeTopBottom: colors.strokeNeutralLight,
  cursor: 'pointer',
  fontWeight: fontWeights.extraBold,
  fontFamily: fonts.sans,
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',

  [darkThemeSelector]: {
    strokeTopBottom: colors.strokeNeutralDark,
  },

  '@notDesktop': {
    heightAll: tableHeadRowHeight['@notDesktop'],
    padding: '$8 $16',
  },

  '@desktop': {
    heightAll: tableHeadRowHeight['@desktop'],
    padding: '$6 $12',
  },

  variants: {
    alignment: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
    canSort: {
      true: {
        outline: 'none',
        cursor: 'pointer',

        [selectors.focus]: {
          '&::before': {
            content: '',
            position: 'absolute',
            inset: '$4',
            display: 'block',
            boxShadow: shadows.focusRingLight,
            borderRadius: '$8',

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {
        cursor: 'default',
      },
    },
    internal: {
      true: {
        color: colors.internalBodyLight,

        [darkThemeSelector]: {
          color: colors.internalBodyDark,
        },
      },
      false: {},
    },
    variant: {
      application: {
        backgroundColor: colors.bgApplicationLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgApplicationDark,
        },
      },
      neutral: {
        backgroundColor: colors.bgNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgNeutralDark,
        },
      },
    },
  },
});

const TableHeadCellContainer = styled('th', {
  position: 'sticky',
  top: 0,
  zIndex: ZINDEX_HEAD_INITIAL,
  padding: 0,
  userSelect: 'none',

  variants: {
    collapsable: {
      true: {
        width: '$36',
      },
      false: {},
    },
    isLeading: {
      true: {
        left: 0,
        zIndex: ZINDEX_HEAD_LEADING,
      },
      false: {},
    },
    isMultiRowSelection: {
      true: {},
      false: {},
    },
    isSorted: {
      true: {
        zIndex: ZINDEX_HEAD_SORTED,
      },
      false: {},
    },
    stuck: {
      top: {
        zIndex: ZINDEX_HEAD_STUCK,
      },
      right: {
        zIndex: ZINDEX_HEAD_STUCK,
        right: 0,
      },
      bottom: {
        zIndex: ZINDEX_HEAD_STUCK,
        bottom: 0,
      },
      left: {
        zIndex: ZINDEX_HEAD_STUCK,
        left: 0,
      },
    },
  },
  compoundVariants: [
    {
      isMultiRowSelection: true,
      isLeading: true,
      css: {
        left: '46px',
      },
    },
  ],
});

export type TableHeadCellTooltipProp = { label?: LabelTooltipProps; icon?: IconTooltipProps };

type TableHeadCellProps = PropsWithChildren<
  HTMLAttributes<HTMLTableCellElement> & {
    /**
     * The alignment of the cell content.
     */
    alignment?: 'start' | 'center' | 'end';
    /**
     * Whether the cell is collapsable.
     */
    collapsable?: boolean;
    /**
     * Whether the cell is collapsed.
     */
    collapsed?: boolean;
    /**
     * Whether to hide the sort icon.
     */
    hideSortIcon?: boolean;
    /**
     * The icon to display in the cell, label is hidden.
     */
    icon?: IconName;
    /**
     * Access level of the cell.
     */
    accessLevel?: AccessLevelProp | null;
    /**
     * Whether the cell is visible only to Meter employees.
     */
    internal?: boolean;
    /**
     * Whether the cell is part of a nested `Table`.
     */
    isNested?: boolean;
    /**
     * Whether the cell is leading.
     */
    isLeading?: boolean;
    /**
     * The sort direction of the cell.
     */
    sortDirection?: 'asc' | 'desc' | boolean;
    /**
     * Whether the cell is stuck to the top, right, bottom, or left of the `Table`.
     */
    stuck?: 'top' | 'right' | 'bottom' | 'left';
    /**
     * Determines both the type and contents of the tooltip to display when the cell is hovered.
     */
    tooltip?: TableHeadCellTooltipProp;
    /**
     * Whether the cell has a field.
     */
    hasField?: boolean;
    /**
     * The group the cell belongs to.
     */
    group?: string | null;
  }
>;

function TableHeadCellInner(
  {
    alignment,
    children,
    collapsable,
    collapsed,
    hideSortIcon,
    icon,
    accessLevel,
    internal,
    isNested,
    isLeading,
    sortDirection,
    stuck,
    tooltip,
    hasField,
    group,
    ...props
  }: TableHeadCellProps,
  ref: React.ForwardedRef<HTMLTableCellElement>,
) {
  const tableVariant = useTableVariant();
  const isMultiRowSelection = useTableMultiRowSelection();
  if (accessLevel === null) return null;

  const tableHeadCellLabel = icon ? <TableHeadCellIcon icon={icon} /> : children;

  let tableHeadCellContents;
  if (tooltip && tooltip.label) {
    tableHeadCellContents = (
      <LabelTooltip
        preset="small"
        internal={internal || accessLevel === AccessLevel.Internal}
        {...tooltip.label}
      >
        {tableHeadCellLabel}
      </LabelTooltip>
    );
  } else if (tooltip && tooltip.icon) {
    tableHeadCellContents = (
      <IconTooltip
        size={16}
        internal={internal || accessLevel === AccessLevel.Internal}
        {...tooltip.icon}
      />
    );
  } else {
    tableHeadCellContents = tableHeadCellLabel;
  }

  const hasSort = !collapsable && (!hideSortIcon || !!sortDirection);

  return (
    <TableHeadCellContainer
      isLeading={isLeading}
      isSorted={!!sortDirection}
      isMultiRowSelection={isMultiRowSelection}
      stuck={stuck}
      {...props}
      ref={ref}
    >
      <TableHeadCellContents
        as={collapsable ? 'button' : 'div'}
        canSort={!hideSortIcon || !!sortDirection}
        tabIndex={collapsable || !hideSortIcon ? 0 : undefined}
        alignment={alignment}
        internal={internal || accessLevel === AccessLevel.Internal}
        variant={tableVariant}
      >
        <AlignStack
          align="center"
          preset="small"
          width="min-content"
          start={
            collapsable && (
              <TableCellCollapsable icon={collapsed ? 'chevron-right' : 'chevron-down'} />
            )
          }
          end={
            (accessLevel && accessLevel !== 'public') || hasSort ? (
              <>
                {accessLevel && accessLevel !== 'public' && (
                  <TableHeadCellBadge>
                    <FeatureBadge size="x-small" type={accessLevel} />
                  </TableHeadCellBadge>
                )}
                {hasSort && (
                  <TableHeadCellSort isVisible={!!sortDirection}>
                    <TableHeadCellSortIcon
                      icon={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
                      size={10}
                    />
                  </TableHeadCellSort>
                )}
              </>
            ) : null
          }
        >
          {tableHeadCellContents}
        </AlignStack>
      </TableHeadCellContents>
    </TableHeadCellContainer>
  );
}

export const TableHeadCell = React.forwardRef(
  TableHeadCellInner,
) as Polymorphic.ForwardRefComponent<React.ElementType, TableCellProps>;

const TableHeadRowContainer = styled('tr', {
  position: 'relative',
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },
});

type TableHeadRowProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>>;

function TableHeadRowPassthrough(
  props: TableHeadRowProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => setIsPinned(e.intersectionRatio < 1), {
      threshold: [1],
    });

    if (innerRef.current) {
      observer.observe(innerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <TableHeadRowContainer {...props} ref={mergeRefs([ref, innerRef])} data-is-pinned={isPinned} />
  );
}

export const TableHeadRow = React.forwardRef<HTMLDivElement, TableHeadCellProps>(
  TableHeadRowPassthrough,
);

export const TableHead = styled('thead');

const TableContainer = styled('table', {
  position: 'relative',
  width: '100%',

  variants: {
    isNested: {
      true: {},
      false: {},
    },
    multiRowSelection: {
      true: {},
      false: {},
    },
  },
});

interface TableProps extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  /**
   * Whether the table is nested within a parent `Table`.
   */
  isNested?: TableNestedProp;
  /**
   * Whether the table allows for multi-row selection.
   */
  multiRowSelection?: boolean;
  /**
   * The variant of table.
   */
  variant?: TableVariantProp;
}

export const Table = React.forwardRef<HTMLDivElement, TableProps>(
  ({ children, isNested, multiRowSelection, variant, ...props }, ref) => {
    const [scrollRef, canScrollX] = useCanScrollX();
    return (
      <TableContainer
        ref={mergeRefs([ref, scrollRef])}
        isNested={isNested}
        data-can-scroll-x={canScrollX}
        {...props}
      >
        <TableMultiRowSelectionProvider value={multiRowSelection}>
          <TableNestedProvider value={isNested}>
            <TableVariantProvider value={variant}>{children}</TableVariantProvider>
          </TableNestedProvider>
        </TableMultiRowSelectionProvider>
      </TableContainer>
    );
  },
);
